import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  UserGroupIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import Table from "../../common/Table";
import CreateNewVolunteer from "./CreateNewVolunteer";
import VolunteerDetails from "./VolunteerDetails";
import EditVolunteer from "./EditVolunteer";
import { communityServicesAPI } from "../../../services/api";
import toast from "react-hot-toast";

const VolunteerRegistrationManagement = forwardRef(
  ({ isOpen, onClose, onSuccess }, ref) => {
    // --- State ---
    const [volunteers, setVolunteers] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedVolunteer, setSelectedVolunteer] = useState(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [volunteerToDelete, setVolunteerToDelete] = useState(null);
    const [filterProgram, setFilterProgram] = useState("All Programs");
    const [filterStatus, setFilterStatus] = useState("All Status");
    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
    });

    // --- Expose refresh function to parent ---
    useImperativeHandle(ref, () => ({
      refresh: () => {
        fetchVolunteers(1, pagination.perPage);
      },
    }));

    // --- Fetch Programs ---
    useEffect(() => {
      const fetchPrograms = async () => {
        try {
          const res = await communityServicesAPI.getVolunteerPrograms();
          if (res.data.success && Array.isArray(res.data.data)) {
            setPrograms(res.data.data[0] || []);
          }
        } catch (err) {
          console.error("Failed to fetch programs:", err);
        }
      };
      fetchPrograms();
    }, []);

    const fetchVolunteers = async (page = 1, perPage = pagination.perPage) => {
      if (!isOpen) return;

      try {
        const selectedProgramObj =
          filterProgram !== "All Programs"
            ? programs.find((p) => p.name === filterProgram)
            : null;

        const params = {
          per_page: perPage,
          page,
          search: search || undefined,
          status: filterStatus !== "All Status" ? filterStatus.toLowerCase() : undefined,
          program_id: selectedProgramObj?.value || undefined,
        };

        const res = await communityServicesAPI.getVolunteersList(params);

        if (res.data.success) {
          const { volunteers: vols, meta } = res.data.data;

          const mappedVolunteers = vols.map((v) => ({
            id: v.id,
            volunteer_no: v.volunteer_no,
            name: v.is_resident ? v.resident.full_name : v.volunteer_name,
            program: v.programs.map((p) => p.name).join(", "),
            contact: v.contact_information,
            status: v.status.charAt(0).toUpperCase() + v.status.slice(1),
            original: v,
          }));

          setVolunteers(mappedVolunteers);
          setPagination({
            currentPage: meta.current_page,
            perPage: meta.per_page,
            total: meta.total,
            lastPage: meta.last_page,
          });
        }
      } catch (err) {
        console.error("Failed to fetch volunteers:", err);
      }
    };

    // --- Fetch on dependencies change ---
    useEffect(() => {
      if (programs.length > 0 || filterProgram === "All Programs") {
        fetchVolunteers(1, pagination.perPage);
      }
    }, [isOpen, search, filterProgram, filterStatus, programs]);

    // --- Handlers ---
    const handleCreateVolunteer = (newData) => {
      const newEntry = { id: Date.now(), ...newData };
      setVolunteers([newEntry, ...volunteers]);
      setIsCreateModalOpen(false);
      if (onSuccess) onSuccess();
    };

    const handleUpdateVolunteer = (updatedData) => {
      const mappedVolunteer = {
        id: updatedData.id,
        volunteer_no: updatedData.volunteer_no,
        name: updatedData.is_resident
          ? updatedData.resident?.full_name
          : updatedData.volunteer_name,
        program: updatedData.programs?.map((p) => p.name).join(", "),
        contact: updatedData.contact_information,
        status:
          updatedData.status.charAt(0).toUpperCase() + updatedData.status.slice(1),
        original: updatedData,
      };

      setVolunteers((prev) =>
        prev.map((v) => (v.id === updatedData.id ? mappedVolunteer : v))
      );

      setIsEditModalOpen(false);
    };


    const handlePageChange = (page, perPage) => {
      setPagination((prev) => ({
        ...prev,
        currentPage: page,
        perPage,
      }));
      fetchVolunteers(page, perPage);
    };

    const handleSearchChange = (value) => setSearch(value);
    const handleFilterProgramChange = (value) => {
      setFilterProgram(value);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    };
    const handleFilterStatusChange = (value) => {
      setFilterStatus(value);
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
    };
    const toggleFilters = () => setShowFilters((prev) => !prev);
    const confirmDeleteVolunteer = (volunteer) => {
      setVolunteerToDelete(volunteer);
      setIsDeleteModalOpen(true);
    };

    const handleDeleteVolunteer = async () => {
      if (!volunteerToDelete) return;

      try {
        const res = await communityServicesAPI.deleteVolunteer(volunteerToDelete.id);

        if (res.data.success) {
          setVolunteers((prev) => prev.filter((v) => v.id !== volunteerToDelete.id));
          setPagination((prev) => ({
            ...prev,
            total: prev.total - 1,
            lastPage: Math.ceil((prev.total - 1) / prev.perPage),
          }));
          toast.success(res.data.message || "Volunteer deleted successfully");
        } else {
          toast.error(res.data.message || "Failed to delete volunteer");
        }
      } catch (err) {
        console.error("Delete volunteer error:", err);
        toast.error("An error occurred while deleting the volunteer");
      } finally {
        setVolunteerToDelete(null);
        setIsDeleteModalOpen(false);
      }
    };

    // --- Table Columns ---
    const columns = [
      {
        key: "volunteer_no",
        label: "Volunteer No.",
        render: (value) => <span className="text-sm">{value}</span>
      },
      {
        key: "name",
        label: "Volunteer Name",
        render: (value) => <span className="text-sm">{value}</span>
      },
      {
        key: "program",
        label: "Program Name",
        render: (value) => <span className="text-sm">{value}</span>
      },
      {
        key: "contact",
        label: "Contact Info",
        render: (value) => <span className="text-sm">{value}</span>
      },
      {
        key: "status",
        label: "Status",
        render: (value) => {
          let colorClasses = "";

          if (value === "Active") colorClasses = "bg-green-100 text-green-700";
          else if (value === "Inactive") colorClasses = "bg-red-100 text-red-700";
          else if (value === "Pending") colorClasses = "bg-yellow-100 text-yellow-700";

          return (
            <span className={`px-4 py-1 rounded-full text-xs font-medium ${colorClasses}`}>
              {value}
            </span>
          );
        },
      },
      {
        key: "actions",
        label: "Actions",
        render: (_, row) => (
          <div className="flex justify-center gap-3 text-sm">
            <button
              onClick={() => {
                setSelectedVolunteer(row.original);
                setIsDetailsOpen(true);
              }}
              className="text-blue-500 hover:text-blue-700"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setSelectedVolunteer(row.original);
                setIsEditModalOpen(true);
              }}
              className="text-orange-400 hover:text-orange-600"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => confirmDeleteVolunteer(row.original)}
              className="text-red-500 hover:text-red-700"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ];


    if (!isOpen) return null;

    return (
      // <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <div className="fixed inset-0 z-50 bg-black/40 p-4">
        {/* Center the modal for small content, allow scroll for long content */}
        <div className="flex min-h-screen items-center justify-center">
          {/* <div className="max-w-6xl w-full bg-white rounded-xl shadow-xl border border-gray-200"> */}
          <div className="max-w-6xl w-full bg-white rounded-xl shadow-xl border border-gray-200 max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h1 className="text-xl font-semibold text-gray-800">
                Volunteer Registration Management
              </h1>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Controls */}
            {/* <div className="p-6 space-y-4"> */}
            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <UserGroupIcon className="w-6 h-6 text-blue-500" />
                  </div>
                  <h2 className="text-lg font-semibold">List of Registered Volunteers</h2>
                </div>

                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                  <div className="relative flex-grow md:flex-grow-0">
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      placeholder="Search volunteers..."
                      value={search}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>

                  <button
                    onClick={toggleFilters}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${showFilters
                      ? "bg-blue-50 border-blue-500 text-blue-600"
                      : "border-gray-300 hover:bg-gray-50 text-gray-700"
                      }`}
                  >
                    <FunnelIcon className="w-5 h-5" />
                    Filters
                  </button>

                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-all shadow-sm"
                  >
                    <PlusIcon className="w-5 h-5" />
                    New Volunteer
                  </button>
                </div>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="space-y-1.5">
                    <label className="text-sm font-bold text-gray-700">Program</label>
                    <div className="relative">
                      <select
                        value={filterProgram}
                        onChange={(e) => handleFilterProgramChange(e.target.value)}
                        className="w-full p-2.5 bg-white border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      >
                        <option>All Programs</option>
                        {programs.map((p) => (
                          <option key={p.value} value={p.name}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDownIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-sm font-bold text-gray-700">Status</label>
                    <div className="relative">
                      <select
                        value={filterStatus}
                        onChange={(e) => handleFilterStatusChange(e.target.value)}
                        className="w-full p-2.5 bg-white border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                      >
                        <option>All Status</option>
                        <option>Active</option>
                        <option>Inactive</option>
                        <option>Pending</option>
                      </select>
                      <ChevronDownIcon className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              <Table
                columns={columns}
                data={volunteers}
                getRowKey={(row) => row.id}
                pagination={{
                  currentPage: pagination.currentPage,
                  lastPage: pagination.lastPage,
                  perPage: pagination.perPage,
                  total: pagination.total,
                }}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>

        {/* Modals */}
        <CreateNewVolunteer
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateVolunteer}
        />

        <EditVolunteer
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onUpdate={handleUpdateVolunteer}
          volunteer={selectedVolunteer}
        />

        <VolunteerDetails
          isOpen={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
          volunteer={selectedVolunteer}
        />

        {isDeleteModalOpen && volunteerToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-xl p-6 w-96 text-center">
              <h2 className="text-lg font-semibold mb-4">Delete Volunteer</h2>
              <p className="mb-6">
                Are you sure you want to delete{" "}
                <span className="font-bold">{volunteerToDelete.name}</span>?
              </p>
              <div className="flex justify-center gap-4">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteVolunteer}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default VolunteerRegistrationManagement;
